import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { License, LicensePeople, ReportingHistory } from 'src/types/licenses';

export interface LicenseViewState {
  isGetReportingHistoryInProgress: boolean;
  isGetReportingHistorySuccess: boolean;
  reportingHistory: ReportingHistory | null;
  reportingHistoryErrorMsg: string;

  isGetPropertyContactsInProgress: boolean;
  isGetPropertyContactsSuccess: boolean;
  propertyContacts: LicensePeople | null;
  propertyContactsErrorMsg: string;

  isGetPermitActionabilityInProgress: boolean;
  isGetPermitActionabilitySuccess: boolean;
  permitActionability: object | null;
  permitActionabilityErrorMsg: string;

  license: License | null;
  licenseInProgress: boolean;
  licenseSuccess: boolean;
}

const INITIAL_STATE: LicenseViewState = {
  isGetReportingHistoryInProgress: false,
  isGetReportingHistorySuccess: false,
  reportingHistory: null,
  reportingHistoryErrorMsg: '',

  isGetPropertyContactsInProgress: false,
  isGetPropertyContactsSuccess: false,
  propertyContacts: null,
  propertyContactsErrorMsg: '',

  isGetPermitActionabilityInProgress: false,
  isGetPermitActionabilitySuccess: false,
  permitActionability: null,
  permitActionabilityErrorMsg: '',

  license: null,
  licenseInProgress: false,
  licenseSuccess: false,
};

export const licenseViewSlice = createSlice({
  name: 'licenseView',
  initialState: INITIAL_STATE,
  reducers: {
    reportingHistoryStarted: (state) => {
      state.isGetReportingHistoryInProgress = true;
      state.isGetReportingHistorySuccess = false;
      state.reportingHistoryErrorMsg = '';
    },
    reportingHistorySucceeded: (
      state,
      action: PayloadAction<ReportingHistory | null>
    ) => {
      state.isGetReportingHistoryInProgress = false;
      state.isGetReportingHistorySuccess = true;
      state.reportingHistory = action.payload;
      state.reportingHistoryErrorMsg = '';
    },
    reportingHistoryFailure: (state, action: PayloadAction<string>) => {
      state.isGetReportingHistoryInProgress = false;
      state.isGetReportingHistorySuccess = false;
      state.reportingHistory = null;
      state.reportingHistoryErrorMsg = action.payload;
    },

    propertyContactsStarted: (state) => {
      state.isGetPropertyContactsInProgress = true;
      state.isGetPropertyContactsSuccess = false;
      state.propertyContactsErrorMsg = '';
    },
    propertyContactsSucceeded: (
      state,
      action: PayloadAction<LicensePeople>
    ) => {
      state.isGetPropertyContactsInProgress = false;
      state.isGetPropertyContactsSuccess = true;
      state.propertyContacts = action.payload;
      state.propertyContactsErrorMsg = '';
    },
    propertyContactsFailure: (state, action: PayloadAction<string>) => {
      state.isGetPropertyContactsInProgress = false;
      state.isGetPropertyContactsSuccess = false;
      state.propertyContacts = null;
      state.propertyContactsErrorMsg = action.payload;
    },

    permitActionabilityStarted: (state) => {
      state.isGetPermitActionabilityInProgress = true;
      state.isGetPermitActionabilitySuccess = false;
      state.permitActionabilityErrorMsg = '';
    },
    permitActionabilitySucceeded: (state, action: PayloadAction<object>) => {
      state.isGetPermitActionabilityInProgress = false;
      state.isGetPermitActionabilitySuccess = true;
      state.permitActionability = action.payload;
      state.permitActionabilityErrorMsg = '';
    },
    permitActionabilityFailure: (state, action: PayloadAction<string>) => {
      state.isGetPermitActionabilityInProgress = false;
      state.isGetPermitActionabilitySuccess = false;
      state.permitActionability = null;
      state.permitActionabilityErrorMsg = action.payload;
    },

    licenseStarted: (state) => {
      state.licenseInProgress = true;
      state.licenseSuccess = false;
      state.license = null;
    },
    licenseSucceeded: (state, action: PayloadAction<License>) => {
      state.licenseInProgress = false;
      state.licenseSuccess = true;
      state.license = action.payload;
    },
    licenseFailure: (state) => {
      state.licenseInProgress = false;
      state.licenseSuccess = false;
      state.license = null;
    },

    resetLicenseViewState: () => INITIAL_STATE,
  },
});

export const {
  reportingHistoryStarted,
  reportingHistorySucceeded,
  reportingHistoryFailure,
  propertyContactsStarted,
  propertyContactsSucceeded,
  propertyContactsFailure,
  permitActionabilityStarted,
  permitActionabilitySucceeded,
  permitActionabilityFailure,
  licenseStarted,
  licenseSucceeded,
  licenseFailure,
  resetLicenseViewState,
} = licenseViewSlice.actions;

export default licenseViewSlice.reducer;
